import * as React from "react";
import { Deployment } from "../../../../schema/types/workload/deployment";
import { Location } from "../../../../schema/types/location/entity";
import { request } from "../../../../services/cpln";

export interface LocationMarker {
  name: string;
  lat: number;
  lon: number;
  city?: string;
  country?: string;
  continent?: string;
  provider: string;
  isReady: boolean;
  replicaCount: number;
  message?: string;
  // Enhanced state fields for deployment status
  expectedVersion?: number;
  latestVersionReady?: boolean;
  latestVersionNumber?: number;
  isInitializing?: boolean;
  isProgressing?: boolean;
  isInactive?: boolean; // For locations not in GVC (gray dots)
  isPending?: boolean; // For GVC locations without deployment data yet (blue dots)
}

interface LocationGeoDataResult {
  markers: LocationMarker[];
  isLoading: boolean;
  error: string | null;
}

interface AllLocationsResult {
  locations: LocationMarker[];
  isLoading: boolean;
  error: string | null;
}

function getReplicaCount(deployment: Deployment): number {
  const versions = deployment.status?.versions || [];
  let count = 0;
  for (const version of versions) {
    if (version.containers) {
      for (const containerName in version.containers) {
        const container = version.containers[containerName];
        count += container.resources?.replicas || 0;
      }
    }
  }
  return count;
}

// Cache for location geo data - persists across renders and component instances
interface LocationGeoCache {
  [locationName: string]: {
    lat: number;
    lon: number;
    city?: string;
    country?: string;
    continent?: string;
    provider: string;
  };
}

export function useLocationGeoData(deployments: Deployment[], org: string): LocationGeoDataResult {
  // Cache location geo data - persists across deployment updates
  const geoCache = React.useRef<LocationGeoCache>({});
  const [geoCacheVersion, setGeoCacheVersion] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Get location names from deployments
  const locationNames = React.useMemo(() => deployments.map((d) => d.name), [deployments]);

  // Create a stable key for location names to use in dependency array
  const locationNamesKey = locationNames.join(",");

  // Fetch geo data ONLY for locations not in cache
  React.useEffect(() => {
    const missingLocations = locationNames.filter((name) => !geoCache.current[name]);

    if (missingLocations.length === 0) return;

    let cancelled = false;

    async function fetchMissingGeoData() {
      setIsLoading(true);
      setError(null);

      try {
        await Promise.all(
          missingLocations.map(async (locationName) => {
            try {
              const { data: location } = await request<Location>({
                url: `/org/${org}/location/${locationName}`,
              });

              if (cancelled) return;

              const geo = location.status?.geo;
              if (geo?.lat !== undefined && geo?.lon !== undefined) {
                geoCache.current[locationName] = {
                  lat: geo.lat,
                  lon: geo.lon,
                  city: geo.city,
                  country: geo.country,
                  continent: geo.continent,
                  provider: location.provider,
                };
              }
            } catch (err) {
              console.warn(`Failed to load location: ${locationName}`, err);
            }
          })
        );

        if (!cancelled) {
          setGeoCacheVersion((v) => v + 1); // Trigger re-render with new cache data
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load location data");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchMissingGeoData();

    return () => {
      cancelled = true;
    };
  }, [locationNamesKey, org]); // Only re-run when location names change

  // Combine cached geo data with deployment status - SYNCHRONOUS
  // This runs instantly when deployments change (e.g., from WebSocket updates)
  const markers = React.useMemo(() => {
    return deployments
      .map((deployment) => {
        const geo = geoCache.current[deployment.name];
        if (!geo) return null; // Geo data not yet loaded

        const status = deployment.status;
        const versions = status?.versions || [];

        // Find latest version (highest workload number)
        let latestVersion: (typeof versions)[0] | undefined;
        for (const v of versions) {
          if (!latestVersion || (v.workload ?? 0) > (latestVersion.workload ?? 0)) {
            latestVersion = v;
          }
        }

        // Check if initializing
        const scheduler = (status as any)?.internal?.scheduler;
        const latestMessage = latestVersion?.message || "";
        const isInitializing = scheduler === "initialize" || latestMessage === "Initializing";

        // Check if progressing
        const statusMessage = status?.message || "";
        const isProgressing = statusMessage.toLowerCase().includes("progressing");

        return {
          name: deployment.name,
          ...geo,
          isReady: status?.ready ?? false,
          replicaCount: getReplicaCount(deployment),
          message: status?.message,
          expectedVersion: status?.expectedDeploymentVersion,
          latestVersionReady: latestVersion?.ready,
          latestVersionNumber: latestVersion?.workload,
          isInitializing,
          isProgressing,
        } as LocationMarker;
      })
      .filter((m): m is LocationMarker => m !== null);
  }, [deployments, geoCacheVersion]); // Re-runs on deployments change OR cache update

  return { markers, isLoading, error };
}

export function useAllOrgLocations(org: string, enabled: boolean): AllLocationsResult {
  const [locations, setLocations] = React.useState<LocationMarker[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!enabled || !org) {
      setLocations([]);
      return;
    }

    let cancelled = false;

    async function fetchAllLocations() {
      setIsLoading(true);
      setError(null);

      try {
        const { data } = await request<{ items: Location[] }>({ url: `/org/${org}/location` });

        if (cancelled) return;

        const results: LocationMarker[] = [];

        for (const location of data.items || []) {
          const geo = location.status?.geo;
          if (geo?.lat !== undefined && geo?.lon !== undefined) {
            results.push({
              name: location.name,
              lat: geo.lat,
              lon: geo.lon,
              city: geo.city,
              country: geo.country,
              continent: geo.continent,
              provider: location.provider,
              isReady: false,
              replicaCount: 0,
              isInactive: true,
            });
          }
        }

        if (!cancelled) {
          setLocations(results);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load org locations");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchAllLocations();

    return () => {
      cancelled = true;
    };
  }, [org, enabled]);

  return { locations, isLoading, error };
}
